import { useEffect, useRef, useState } from "react";

/**
 * A live "trading bot" chat that appends a new line every 1-3 seconds, 24/7/365.
 * Purely cosmetic — makes the portfolio feel like a robot is always working.
 * Also renders "OPEN PANEL" (current open positions summary) and "OPEN PORTIONS"
 * (allocation slice) headers as requested.
 */

const SYMBOLS = ["BTC", "ETH", "SOL", "XRP", "ADA", "AVAX", "LINK", "DOT", "AAPL", "NVDA", "TSLA", "GOLD", "SILVER", "EURUSD", "USDJPY"];
const SIDES = ["LONG", "SHORT"];
const ACTIONS = [
  (sym) => ({ label: "SCAN",   text: `Scanning ${sym} momentum on 1m — RSI 58, MACD +`, tone: "muted" }),
  (sym) => ({ label: "OPEN",   text: `${randSide()} ${sym} @ ${price(sym)} · size ${size(sym)} · TP ${(1 + Math.random() * 2).toFixed(2)}%`, tone: "buy" }),
  (sym) => ({ label: "CLOSE",  text: `Exited ${sym} · +${(Math.random() * 380 + 12).toFixed(2)} USDT locked`, tone: "sell" }),
  (sym) => ({ label: "SIGNAL", text: `${sym} broke resistance at ${price(sym)} — trailing stop armed`, tone: "muted" }),
  (sym) => ({ label: "HEDGE",  text: `Hedging ${sym} exposure with inverse contract, delta neutralised`, tone: "muted" }),
  (sym) => ({ label: "FILL",   text: `Filled ${sym} ${randSide()} @ ${price(sym)} — slippage 0.02%`, tone: "buy" }),
  (sym) => ({ label: "ALERT",  text: `Whale wallet moved 8.2M USDT into ${sym} — accumulation flagged`, tone: "muted" }),
  (sym) => ({ label: "MODEL",  text: `Model refreshed on ${sym} · confidence ${(70 + Math.random() * 27).toFixed(1)}%`, tone: "muted" }),
];

function randSide() { return SIDES[(Math.random() * SIDES.length) | 0]; }
function price(sym) {
  const base = { BTC: 76300, ETH: 2370, SOL: 118, XRP: 0.53, ADA: 0.35, AVAX: 27, LINK: 12.7, DOT: 4.8, AAPL: 213, NVDA: 128, TSLA: 244, GOLD: 4317, SILVER: 63, EURUSD: 1.086, USDJPY: 151.4 }[sym] || 100;
  const jitter = base * (1 + (Math.random() - 0.5) * 0.02);
  return jitter >= 1000 ? jitter.toFixed(0) : jitter.toFixed(2);
}
function size(sym) {
  if (["BTC", "ETH"].includes(sym)) return (Math.random() * 0.5 + 0.02).toFixed(4);
  if (["AAPL", "NVDA", "TSLA"].includes(sym)) return (Math.random() * 50 + 5).toFixed(0);
  return (Math.random() * 400 + 20).toFixed(0);
}
function stamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function makeLine(id) {
  const sym = SYMBOLS[(Math.random() * SYMBOLS.length) | 0];
  const build = ACTIONS[(Math.random() * ACTIONS.length) | 0];
  const { label, text, tone } = build(sym);
  return { id, t: stamp(), label, text, tone, sym };
}

export default function TradingChat({ openPositions = [], equity = 0 }) {
  const [lines, setLines] = useState(() => Array.from({ length: 8 }, (_, i) => makeLine(`seed-${i}`)));
  const idRef = useRef(1000);
  const scrollRef = useRef(null);

  useEffect(() => {
    let t;
    const tick = () => {
      idRef.current += 1;
      setLines((cur) => {
        const next = [...cur, makeLine(`l-${idRef.current}`)];
        return next.slice(-80);  // rolling window
      });
      t = setTimeout(tick, 900 + Math.random() * 1800);
    };
    t = setTimeout(tick, 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [lines.length]);

  const openCount = openPositions?.length || 0;
  const totalNotional = (openPositions || []).reduce((s, p) => s + Math.abs(p.qty * (p.mark_price || p.avg_price || 0)), 0);
  const portions = (openPositions || []).map((p) => ({
    sym: p.symbol,
    pct: totalNotional > 0 ? (Math.abs(p.qty * (p.mark_price || p.avg_price || 0)) / totalNotional) * 100 : 0,
  }));

  return (
    <div className="border border-white/10 bg-surface" data-testid="trading-chat">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00FF41] opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00FF41]" />
          </span>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/70">Trading desk · autonomous</p>
        </div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">24 / 7 / 365</p>
      </div>

      {/* Open panel + open portions header */}
      <div className="grid grid-cols-1 gap-4 border-b border-white/5 px-5 py-4 md:grid-cols-2">
        <div>
          <p className="text-[9px] uppercase tracking-[0.28em] text-white/40">Open panel</p>
          <p className="mt-1 font-tabular text-lg text-white/85">{openCount} positions</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">
            Notional <span className="font-tabular text-gold">${totalNotional.toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
          </p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-[0.28em] text-white/40">Open portions</p>
          {portions.length === 0 ? (
            <p className="mt-1 text-[10px] text-white/35">No exposure — scanning</p>
          ) : (
            <div className="mt-1 flex h-2 w-full overflow-hidden bg-white/5">
              {portions.slice(0, 6).map((p, i) => (
                <div key={p.sym} title={`${p.sym} ${p.pct.toFixed(1)}%`}
                     style={{ width: `${p.pct}%`, background: ["#F0CE6A", "#26A69A", "#EF5350", "#8892b0", "#9945FF", "#F7931A"][i % 6] }} />
              ))}
            </div>
          )}
          {portions.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-white/50">
              {portions.slice(0, 6).map((p) => (
                <span key={p.sym}>{p.sym} <span className="font-tabular text-gold">{p.pct.toFixed(1)}%</span></span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="h-56 overflow-y-auto px-5 py-3 font-mono text-[11px] leading-relaxed"
        data-testid="trading-chat-log"
      >
        {lines.map((l) => (
          <div key={l.id} className="grid grid-cols-[64px_60px_1fr] gap-2 py-0.5">
            <span className="text-white/30">{l.t}</span>
            <span
              className={
                l.tone === "buy" ? "font-semibold text-[#26A69A]" :
                l.tone === "sell" ? "font-semibold text-[#EF5350]" :
                "font-semibold text-gold/80"
              }
            >
              {l.label}
            </span>
            <span className="text-white/70">{l.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
